import { useEffect } from 'react'
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react'

const ConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Confirm Action',
    message = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    type = 'warning', // 'warning', 'danger', 'info', 'success'
    showCancel = true
}) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [isOpen])

    if (!isOpen) return null

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose()
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onClose()
        }
    }

    const iconMap = {
        warning: <AlertTriangle className="w-6 h-6 text-yellow-400" />,
        danger: <AlertCircle className="w-6 h-6 text-red-400" />,
        info: <Info className="w-6 h-6 text-blue-400" />,
        success: <CheckCircle className="w-6 h-6 text-green-400" />
    }

    const buttonColorMap = {
        warning: 'bg-yellow-500 hover:bg-yellow-600',
        danger: 'bg-red-500 hover:bg-red-600',
        info: 'bg-blue-500 hover:bg-blue-600',
        success: 'bg-green-500 hover:bg-green-600'
    }

    const defaultButtonColor = 'bg-primary-500 hover:bg-primary-600'

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={handleOverlayClick}
            onKeyDown={handleKeyDown}
        >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            {/* Modal */}
            <div className="relative bg-secondary-800 border border-secondary-700 rounded-xl shadow-2xl w-full max-w-md transform transition-transform">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-secondary-700">
                    <div className="flex items-center space-x-3">
                        {iconMap[type]}
                        <h2 className="text-xl font-semibold text-white">{title}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-secondary-400 hover:text-white p-1 transition-colors"
                        title="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-secondary-300">{message}</p>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-secondary-700">
                    {showCancel && (
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-secondary-300 hover:text-white transition-colors"
                        >
                            {cancelText}
                        </button>
                    )}
                    <button
                        onClick={() => {
                            onConfirm()
                            onClose()
                        }}
                        className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors ${buttonColorMap[type] || defaultButtonColor}`}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ConfirmationModal

